// 최근 접속한 원격지 코드(즉석 접속 칸에서 다시 고르기용).
//
// - **접속에 성공했을 때만** 기억한다(`useSessionManager` 의 영상 도착 시점) — 틀린 코드·오타가 목록에 쌓이지 않게.
// - 🔴 **비밀번호는 저장하지 않는다.** 코드와 마지막 접속 시각만 — 세션 비밀번호는 매번 바뀌고, 무인 비밀번호는 비밀이다.
// - 저장소 = localStorage(이 뷰어/앱에만, 다른 기기로 가지 않는다). 세션 창과 로그인 창이 같은 출처라 함께 본다.
//   브라우저 저장이 막힌 환경(사생활 보호 창 등)에서는 조용히 빈 목록 — 기능이 없을 뿐 접속은 된다.

/** localStorage 키. */
export const RECENT_CODES_KEY = 'rs.recentCodes';
/** 기억하는 최대 개수(오래된 것부터 버린다). */
export const RECENT_CODES_MAX = 10;

export interface RecentCode {
  /** `123-456-789` 꼴로 정리한 코드. */
  code: string;
  /** 마지막으로 접속에 성공한 시각(ms). */
  at: number;
}

/** 저장소(시험에서 바꿔 끼운다). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** 코드 정리 — 숫자만 9자리면 `3-3-3` 으로(사람이 `617960764`·`617 960 764` 로 넣어도 같은 항목). 아니면 앞뒤 공백만 뗀다. */
export function normalizeCode(raw: string): string {
  const t = raw.trim();
  const digits = t.replace(/[\s-]/g, '');
  return /^\d{9}$/.test(digits) ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}` : t;
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** 최근 목록(최신 먼저). 저장 값이 깨져 있으면 빈 목록. */
export function loadRecentCodes(store: KeyValueStore | null = defaultStore()): RecentCode[] {
  try {
    const raw = store?.getItem(RECENT_CODES_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(v)) return [];
    return v
      .filter((e): e is RecentCode => !!e && typeof e.code === 'string' && e.code !== '' && typeof e.at === 'number')
      .slice(0, RECENT_CODES_MAX);
  } catch {
    return [];
  }
}

function save(list: RecentCode[], store: KeyValueStore | null): void {
  try {
    store?.setItem(RECENT_CODES_KEY, JSON.stringify(list.slice(0, RECENT_CODES_MAX)));
  } catch {
    // 저장이 막힌 환경 — 기능만 없다.
  }
}

/** 접속 성공 — 맨 앞으로(이미 있으면 옮긴다). */
export function rememberCode(code: string, now = Date.now(), store: KeyValueStore | null = defaultStore()): RecentCode[] {
  const c = normalizeCode(code);
  if (!c) return loadRecentCodes(store);
  const list = [{ code: c, at: now }, ...loadRecentCodes(store).filter((e) => e.code !== c)].slice(0, RECENT_CODES_MAX);
  save(list, store);
  return list;
}

/**
 * 기억할 만한 원격지 코드인가 — 정리했을 때 `123-456-789` 꼴이면. 로그인 접속은 [기기] 목록에서 내부 ID(UUID)로도 들어오는데
 * 그것은 사람이 다시 입력할 값이 아니라 목록에 넣지 않는다(2026-10-23 — 로그인 후 [연결] 화면에도 최근 목록, 사용자 요청).
 */
export function isRememberableCode(raw: string): boolean {
  return /^\d{3}-\d{3}-\d{3}$/.test(normalizeCode(raw));
}

/** 목록에서 지우기(사람이 × 를 눌렀을 때). */
export function forgetCode(code: string, store: KeyValueStore | null = defaultStore()): RecentCode[] {
  const list = loadRecentCodes(store).filter((e) => e.code !== code);
  save(list, store);
  return list;
}
